import * as React from "react";
import { createContext, useCallback, useContext, useState } from "react";
import { Banner, BannerMulti } from "./banner";

interface BannerData {
  id: string;
  variant: "info" | "success" | "warning" | "critical" | "danger";
  title?: string;
  description?: string;
  subtitle?: string;
  isMulti?: boolean;
  duration?: number;
  buttonActionTitle?: string;
  showButtonAction?: boolean;
}

interface BannerContextType {
  showBanner: (banner: Omit<BannerData, "id">) => string;
  dismissBanner: (id: string) => void;
  dismissAll: () => void;
  dismissOthers: (id: string) => void;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

export function BannerProvider({ children }: { children: React.ReactNode }) {
  const [banners, setBanners] = useState<BannerData[]>([]);

  const dismissBanner = useCallback((id: string) => {
    setBanners((prev) => prev.filter((banner) => banner.id !== id));
  }, []);

  const showBanner = useCallback(
    (bannerData: Omit<BannerData, "id">) => {
      const id = Math.random().toString(36).substr(2, 9);
      const banner: BannerData = { ...bannerData, id };

      // Always replace the current banner with the new one
      // This creates a "one banner at a time" behavior
      setBanners([banner]);

      // Auto dismiss after duration (default 5000ms)
      // if (bannerData.duration !== 0) {
      //   setTimeout(() => {
      //     dismissBanner(id);
      //   }, bannerData.duration || 5000);
      // }

      return id;
    },
    [dismissBanner]
  );

  const dismissAll = useCallback(() => {
    setBanners([]);
  }, []);

  const dismissOthers = useCallback((id: string) => {
    setBanners((prev) => prev.filter((banner) => banner.id === id));
  }, []);

  return (
    <BannerContext.Provider
      value={{ showBanner, dismissBanner, dismissAll, dismissOthers }}
    >
      {children}
      <BannerContainer banners={banners} onDismiss={dismissBanner} />
    </BannerContext.Provider>
  );
}

function BannerContainer({
  banners,
  onDismiss,
}: {
  banners: BannerData[];
  onDismiss: (id: string) => void;
}) {
  return (
    <>
      {banners.map((banner) => (
        <div key={banner.id}>
          {banner.isMulti ? (
            <BannerMulti
              variant={banner.variant as "warning" | "critical" | "danger"}
              title={banner.title}
              subtitle={banner.subtitle}
              buttonActionTitle={banner.buttonActionTitle}
              onClose={() => onDismiss(banner.id)}
              className="wl-show"
            />
          ) : (
            <Banner
              variant={banner.variant}
              title={banner.title}
              description={banner.description}
              onClose={() => onDismiss(banner.id)}
              className="wl-show"
              showButtonAction={banner.showButtonAction}
              buttonActionTitle={banner.buttonActionTitle}
            />
          )}
        </div>
      ))}
    </>
  );
}

export function useBanner() {
  const context = useContext(BannerContext);
  if (context === undefined) {
    throw new Error("useBanner must be used within a BannerProvider");
  }
  return context;
}
